import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, Search } from "lucide-react";
import { motion } from "framer-motion";

export function HeroSection() {
  const [domainName, setDomainName] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (domainName.trim()) {
      navigate(`/search-results?domain=${encodeURIComponent(domainName.trim())}`);
    }
  };

  return (
    <section className="relative overflow-hidden bg-background py-16 sm:py-24 lg:py-32">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("https://storage.googleapis.com/fenado-ai-farm-public/generated/1db44e4c-82d3-45d6-9904-604bf39e17d3.webp")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/40 to-background/90"></div>
      </div>

      {/* Decorative elements */}
      <motion.div 
        className="absolute top-1/4 left-10 w-12 h-12 rounded-full bg-primary/10"
        animate={{ y: [0, -15, 0] }}
        transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-1/4 right-10 w-16 h-16 rounded-full bg-secondary/10"
        animate={{ y: [0, -20, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />
      <motion.div 
        className="absolute top-1/3 right-1/4 w-8 h-8 rounded-full bg-primary/5"
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <motion.h1 
            className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Find Your <span className="text-gradient">Perfect Domain</span> Name
          </motion.h1>
          <motion.p 
            className="mt-6 text-lg text-muted-foreground"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Start your online journey with our powerful search. Get .com domains for just $5.98!
          </motion.p>

          <motion.div 
            className="mt-10 max-w-xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <form onSubmit={handleSearch} className="relative">
              <div className="flex shadow-lg rounded-lg overflow-hidden">
                <Input
                  type="text"
                  placeholder="Find your new domain name..."
                  className="flex-grow py-6 px-6 text-lg border-0 focus-visible:ring-0 focus-visible:ring-offset-0 domain-search-input"
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                />
                <Button 
                  type="submit" 
                  size="lg"
                  className="px-6 rounded-l-none bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <Search className="mr-2 h-5 w-5" />
                  Search
                </Button>
              </div>
            </form>
            
            <div className="mt-4 flex flex-wrap justify-center gap-2 text-sm text-muted-foreground">
              <span>Popular TLDs:</span>
              <span className="font-medium text-foreground">.com $5.98</span>
              <span>•</span>
              <span className="font-medium text-foreground">.net $8.98</span>
              <span>•</span>
              <span className="font-medium text-foreground">.org $7.98</span>
              <span>•</span>
              <span className="font-medium text-foreground">.io $32.98</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}